import WasmController from "react-lib/frameworks/WasmController";

// APIs
import PatientDetailView from "issara-sdk/apis/PatientDetailView_apps_REG";
import PatientAppointmentView from "issara-sdk/apis/PatientAppointmentView_apps_QUE";
import PackageOrderList from "issara-sdk/apis/PackageOrderList_core";
import PatientQRCheckInView from "issara-sdk/apis/PatientQRCheckInView_core";
import PatientCurrentEncounterView from "issara-sdk/apis/PatientCurrentEncounterView_core";

// ======================= Types =======================
export type Page =
  | "greetings"
  | "searching"
  | "patientNotFound"
  | "noAppNoPKFound"
  | "noAppPKList"
  | "appToday"
  | "appTodayAppointment";

export type Props = {};

export type State = {
  currentPage?: Page;
  loadingLogin?: boolean;
  // AppInitialize sequence
  AppInitializeSequence?: Record<string, any>;
  // CheckIn data
  patientData?: Record<string, any>;
  packageList?: Record<string, any>[];
  appointmentList?: Record<string, any>[];
  patientCurrentEncounter?: any;
};

export type Event =
  | { message: "RunSequence"; params: any }
  | { message: "HandleLoginSuccess"; params: { login: any; verify?: any; onSuccess?: () => void } }
  | { message: "ScanQR"; params: { data: string } }
  | { message: "CheckPackage"; params: { patientId: number } }
  | { message: "CheckAppointment"; params: { patientId: number } }
  | { message: "NotifyMobileQRCheckIn"; params?: {} }
  | { message: "GetPatientCurrentEncounter"; params: { patientId: number } }
  | { message: "BackToLobby"; params?: {} };

export type Data = {
  division?: number;
};

export const DataInitial: Data = {};

const SESSION_TIMEOUT = 15000;

// ======================= Controller Type =======================
type Controller = WasmController<State, Event, Data, Props>;

// ======================= Helpers =======================
let timeoutId: ReturnType<typeof setTimeout> | null = null;

const startTimeout = (controller: Controller) => {
  if (timeoutId) clearTimeout(timeoutId);

  timeoutId = setTimeout(() => {
    BackToLobby(controller);
  }, SESSION_TIMEOUT);
};

// ======================= Event Handlers =======================
export const ScanQR = async (controller: Controller, params: { data: string }) => {
  const { data } = params;

  if (!data) return;

  controller.setState({ currentPage: "searching" });

  await SearchPatient(controller, { patientId: data });
};

export const SearchPatient = async (controller: Controller, params: { patientId: number }) => {
  const { patientId } = params;

  if (!patientId) return;

  const [patientResp, patientErr] = await PatientDetailView.retrieve({
    apiToken: controller.apiToken,
    pk: patientId,
    extra: { division: controller.data.division },
  });

  if (patientErr) {
    console.warn("[CheckIn] Patient detail error:", patientErr);
    controller.setState({ currentPage: "patientNotFound" });
    startTimeout(controller);
    return;
  }

  controller.setState({ patientData: patientResp });

  await NotifyMobileQRCheckIn(controller, { patientId: patientResp.id });

  startTimeout(controller);
};

export const NotifyMobileQRCheckIn = async (
  controller: Controller,
  params?: { patientId?: number },
) => {
  const patientId = params?.patientId ?? controller.getState().patientData?.id;
  if (!patientId) return;

  const [resp] = await PatientQRCheckInView.post({
    pk: patientId,
    data: {},
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  if (resp?.redirect_to === "select_package") {
    await CheckPackage(controller, { patientId });
  } else if (resp?.redirect_to === "encounter_detail") {
    controller.setState({ currentPage: "appToday" });
  }
};

export const GetPatientCurrentEncounter = async (
  controller: Controller,
  params: { patientId: number },
) => {
  const { patientId } = params;
  if (!patientId) return;

  const [resp] = await PatientCurrentEncounterView.get({
    pk: patientId,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  controller.setState({
    patientCurrentEncounter: resp,
  });

  startTimeout(controller);
};

export const CheckPackage = async (controller: Controller, params: { patientId: number }) => {
  const { patientId } = params;
  if (!patientId) return;

  const [pkgResp, pkgErr] = await PackageOrderList.list({
    apiToken: controller.apiToken,
    params: {
      patient: patientId,
      statuses: "PAID",
      usage_status: "INCOMPLETED",
    },
    extra: { division: controller.data.division },
  });

  if (pkgErr) {
    console.warn("[CheckIn] Package error:", pkgErr);
    controller.setState({ currentPage: "noAppNoPKFound" });
    startTimeout(controller);
    return;
  }

  const packages = pkgResp?.items || pkgResp?.results || [];
  controller.setState({ packageList: packages });

  if (packages.length === 0) {
    // ไม่มี package → ไม่พบแพ็กเกจ
    controller.setState({ currentPage: "noAppNoPKFound" });
    startTimeout(controller);
    return;
  }

  // มี package → เช็ค appointment ต่อ
  await CheckAppointment(controller, { patientId });
};

export const CheckAppointment = async (controller: Controller, params: { patientId: number }) => {
  const { patientId } = params;
  if (!patientId) return;

  const [appResp, appErr] = await PatientAppointmentView.list({
    apiToken: controller.apiToken,
    params: {
      patient_id: patientId,
      exclude_cancel: true,
      status: "1,2",
      appointment_related_type: "PACKAGE",
    },
    extra: { division: controller.data.division },
  });

  if (appErr) {
    console.warn("[CheckIn] Appointment error:", appErr);
    controller.setState({ currentPage: "noAppPKList" });
    startTimeout(controller);
    return;
  }

  const appointments = appResp?.items || [];
  controller.setState({ appointmentList: appointments });

  if (appointments.length > 0) {
    controller.setState({ currentPage: "appTodayAppointment" });
  } else {
    controller.setState({ currentPage: "noAppPKList" });
  }

  startTimeout(controller);
};

export const BackToLobby = (controller: Controller) => {
  if (timeoutId) clearTimeout(timeoutId);

  controller.setState({
    currentPage: "greetings",
    patientData: undefined,
    packageList: undefined,
    appointmentList: undefined,
    patientCurrentEncounter: undefined,
  });
};
